import { createContext, type PropsWithChildren, useContext, useState } from 'react';
import { createAutomaticPresentationCoordinator } from './createAutomaticPresentationCoordinator';
import { type AutomaticPresentationCoordinator } from './types';

const AutomaticPresentationContext = createContext<AutomaticPresentationCoordinator | null>(null);

// Не даёт кандидату с зависшими данными или отсутствующей DOM-целью навсегда остановить очередь.
// Это ограничение времени ожидания, а не пауза между готовыми показами.
const DEFAULT_WAITING_CANDIDATE_TIMEOUT_MS = 10_000;

export type AutomaticPresentationProviderProps = PropsWithChildren;

/**
 * Создаёт общий coordinator для всех автоматических показов внутри приложения.
 * Один и тот же экземпляр сохраняется на весь срок жизни provider и передаётся всем дочерним интеграциям.
 *
 * @kind component
 */
export function AutomaticPresentationProvider({ children }: AutomaticPresentationProviderProps) {
    const [coordinator] = useState(() =>
        createAutomaticPresentationCoordinator({
            waitingCandidateTimeoutMs: DEFAULT_WAITING_CANDIDATE_TIMEOUT_MS,
        })
    );

    return (
        <AutomaticPresentationContext.Provider value={coordinator}>{children}</AutomaticPresentationContext.Provider>
    );
}

/**
 * Возвращает coordinator текущего приложения для React-адаптеров и интеграций с внешними поверхностями.
 *
 * @kind helper
 * @returns Экземпляр coordinator, принадлежащий ближайшему `AutomaticPresentationProvider`.
 */
export function useAutomaticPresentationCoordinator(): AutomaticPresentationCoordinator {
    const coordinator = useContext(AutomaticPresentationContext);

    if (!coordinator) {
        throw new Error(
            'Контекст автоматических показов не инициализирован: добавьте AutomaticPresentationProvider выше в дереве'
        );
    }

    return coordinator;
}
